import React, { useState, useEffect, useCallback } from 'react';
import { formatDistanceToNow } from 'date-fns';
import './Admin.css';

// Fields the admin reviews. `critical` matches the verify_agent's CRITICAL_FIELDS
// — these are the ones the auto-apply path requires both agents to agree on,
// and the ones the owner is most legally exposed if wrong.
const FIELDS = [
  { key: 'case_name', label: 'Case name', critical: true },
  { key: 'defendant', label: 'Defendant', critical: true },
  { key: 'applicant', label: 'Applicant' },
  { key: 'court', label: 'Court' },
  { key: 'file_number', label: 'File number' },
  { key: 'status', label: 'Status' },
  { key: 'description', label: 'Description', textarea: true },
  { key: 'eligibility_criteria', label: 'Eligibility criteria', critical: true, textarea: true },
  { key: 'claim_deadline', label: 'Claim deadline', critical: true, type: 'date' },
  { key: 'settlement_amount', label: 'Settlement amount' },
  { key: 'claim_portal_url', label: 'Claim portal URL', critical: true },
  { key: 'law_firm', label: 'Law firm' },
];

// Pull `value` from a `{value, source_quote}` field pair.
function fieldValue(extraction, field) {
  if (!extraction) return null;
  const f = extraction[field];
  if (f && typeof f === 'object') return f.value ?? null;
  return null;
}

function fieldQuote(extraction, field) {
  if (!extraction) return null;
  const f = extraction[field];
  if (f && typeof f === 'object') return f.source_quote ?? null;
  return null;
}

function trunc(s, n) {
  if (!s) return '';
  return s.length > n ? s.substring(0, n) + '…' : s;
}

function relTime(iso) {
  if (!iso) return null;
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return iso; }
}

export default function Admin({ api, onError }) {
  const [tab, setTab] = useState('pending');
  const [reviews, setReviews] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [overrides, setOverrides] = useState({}); // { caseId: { field: value } }

  const fetchReviews = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get('/api/admin/reviews');
      setReviews(r.data.reviews || []);
    } catch (e) {
      onError?.('Failed to load review queue');
    } finally {
      setLoading(false);
    }
  }, [api, onError]);

  const fetchAudit = useCallback(async () => {
    try {
      const r = await api.get('/api/admin/audit');
      setAudit(r.data.entries || []);
    } catch {
      onError?.('Failed to load audit log');
    }
  }, [api, onError]);

  useEffect(() => { fetchReviews(); fetchAudit(); }, [fetchReviews, fetchAudit]);

  const setOverride = (caseId, field, value) =>
    setOverrides(prev => ({ ...prev, [caseId]: { ...(prev[caseId] || {}), [field]: value } }));

  const approve = async (review) => {
    try {
      const values = overrides[review.id] || {};
      await api.post(`/api/admin/reviews/${review.id}/approve`, { values });
      setOverrides(prev => { const n = { ...prev }; delete n[review.id]; return n; });
      setActiveId(null);
      await Promise.all([fetchReviews(), fetchAudit()]);
    } catch (e) {
      onError?.('Approve failed: ' + (e.response?.data?.error || e.message));
    }
  };

  const reject = async (review) => {
    if (!window.confirm(`Reject "${review.case_name || 'this case'}"? It will not reappear in the queue.`)) return;
    try {
      await api.post(`/api/admin/reviews/${review.id}/reject`);
      setActiveId(null);
      await fetchReviews();
    } catch {
      onError?.('Reject failed');
    }
  };

  return (
    <div className="admin-root">
      <h2>Admin · Review Queue</h2>
      <div className="admin-tabs">
        <button className={`admin-tab ${tab === 'pending' ? 'active' : ''}`} onClick={() => setTab('pending')}>
          Pending review ({reviews.length})
        </button>
        <button className={`admin-tab ${tab === 'audit' ? 'active' : ''}`} onClick={() => setTab('audit')}>
          Audit log
        </button>
      </div>

      {tab === 'pending' && (
        <>
          {loading && <p>Loading…</p>}
          {!loading && reviews.length === 0 && (
            <p className="admin-empty">No cases awaiting review.</p>
          )}
          {reviews.map(rev => (
            <ReviewCard
              key={rev.id}
              review={rev}
              isActive={activeId === rev.id}
              onToggle={() => setActiveId(activeId === rev.id ? null : rev.id)}
              overrides={overrides[rev.id] || {}}
              setOverride={(field, value) => setOverride(rev.id, field, value)}
              onApprove={() => approve(rev)}
              onReject={() => reject(rev)}
            />
          ))}
        </>
      )}

      {tab === 'audit' && <AuditLogTable entries={audit} />}
    </div>
  );
}

function ReviewCard({ review, isActive, onToggle, overrides, setOverride, onApprove, onReject }) {
  const c = review.current || {};
  const ag1 = review.agent_1_extraction;
  const ag2 = review.agent_2_extraction;
  const fetched = relTime(review.source_fetched_at);

  return (
    <div className="admin-card">
      <div className="admin-card-head" onClick={onToggle}>
        <div>
          <strong>{c.case_name || '(unnamed case)'}</strong>
          <span className="admin-card-defendant"> vs {c.defendant || '(unknown)'}</span>
        </div>
        <div className="admin-card-meta">
          {fetched && <span>Fetched {fetched}</span>}
          {review.source_url && (
            <a href={review.source_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
              source
            </a>
          )}
          <span>{isActive ? '▾' : '▸'}</span>
        </div>
      </div>
      {isActive && (
        <div className="admin-card-body">
          <table className="admin-diff-table">
            <thead>
              <tr>
                <th>Field</th>
                <th>Current</th>
                <th>Agent 1</th>
                <th>Agent 2</th>
                <th>Final value (editable)</th>
              </tr>
            </thead>
            <tbody>
              {FIELDS.map(({ key, label, critical, textarea, type }) => {
                const cur = c[key];
                const a1v = fieldValue(ag1, key);
                const a1q = fieldQuote(ag1, key);
                const a2v = fieldValue(ag2, key);
                const a2q = fieldQuote(ag2, key);
                const ovd = overrides[key];
                const finalV = ovd !== undefined ? ovd : (a1v ?? cur ?? '');
                const disagree = (a1v ?? '') !== (a2v ?? '');
                return (
                  <tr key={key} className={critical ? 'admin-row-critical' : ''}>
                    <td>
                      {label}
                      {critical && <span className="admin-critical-mark">*</span>}
                    </td>
                    <td><code>{cur ?? '—'}</code></td>
                    <td className={disagree ? 'admin-cell-disagree' : ''}>
                      <code>{a1v ?? '—'}</code>
                      {a1q && <small>"{trunc(a1q, 80)}"</small>}
                    </td>
                    <td className={disagree ? 'admin-cell-disagree' : ''}>
                      <code>{a2v ?? '—'}</code>
                      {a2q && <small>"{trunc(a2q, 80)}"</small>}
                    </td>
                    <td>
                      {textarea ? (
                        <textarea
                          value={finalV ?? ''}
                          onChange={e => setOverride(key, e.target.value)}
                          rows={3}
                        />
                      ) : (
                        <input
                          type={type || 'text'}
                          value={finalV ?? ''}
                          onChange={e => setOverride(key, e.target.value)}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="admin-card-actions">
            <button className="admin-btn admin-btn-reject" onClick={onReject}>Reject</button>
            <button className="admin-btn admin-btn-approve" onClick={onApprove}>Approve</button>
          </div>
        </div>
      )}
    </div>
  );
}

function AuditLogTable({ entries }) {
  if (!entries.length) return <p className="admin-empty">No audit entries yet.</p>;
  return (
    <table className="admin-audit-table">
      <thead>
        <tr>
          <th>When</th>
          <th>Case</th>
          <th>Field</th>
          <th>Old</th>
          <th>New</th>
          <th>Source</th>
        </tr>
      </thead>
      <tbody>
        {entries.map(e => (
          <tr key={e.id}>
            <td>{relTime(e.applied_at) || ''}</td>
            <td>#{e.class_action_id}</td>
            <td><strong>{e.field_name}</strong></td>
            <td><code>{trunc(e.old_value, 60)}</code></td>
            <td><code>{trunc(e.new_value, 60)}</code></td>
            <td>
              {e.source_url && (
                <a href={e.source_url} target="_blank" rel="noopener noreferrer">link</a>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
